import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Components
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';

// Utils
import { getProjectCode } from '../../utils/auth';
import { ProjectDetail, CommonUtils } from '../../utils/commonUtils';
import { getMembershipDetail, getMembershipList } from '../../utils/membership';
import { MembershipFormUtils } from '../../utils/membershipCommon';
import { 
  Users, 
  Settings,
  Package,
  Store,
  Stamp,
  Coins,
  Gift,
  UserPlus,
  AlertCircle,
  Plus,
  Edit2,
  ArrowUp,
  ArrowDown
} from 'lucide-react';

interface MembershipStats {
  totalMembers: number;
  activeMembers: number;
  newMembersThisMonth: number;
  totalStamps: number;
  totalPoints: number;
  stampRedemptions: number;
  pointRedemptions: number;
}

interface MembershipConfig {
  rewardType: string; // 'STAMP', 'POINT', 'BOTH', 'NONE', 'CHOICE'
  // 스탬프 관련 필드
  stampSetCount?: number;
  voucherExchangeMethod?: string;
  // 포인트 관련 필드
  pointRatio?: number;
}

interface DailyMember {
  date: string;
  count: number;
}

interface TopStore {
  storeCode: string;
  storeName: string;
  memberCount: number;
  growthRate: number;
}

interface BenefitProduct {
  productCode: string;
  productName: string;
  benefitType: 'stamp' | 'point';
  benefitValue: number;
}

interface GenderRatio {
  male: number;
  female: number;
}

const MembershipDashboard: React.FC = () => {
  // Router hooks
  const navigate = useNavigate();

  // UI state
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('membership');

  // Project state
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  // Stats state
  const [stats, setStats] = useState<MembershipStats>({
    totalMembers: 0,
    activeMembers: 0,
    newMembersThisMonth: 0,
    totalStamps: 0,
    totalPoints: 0,
    stampRedemptions: 0,
    pointRedemptions: 0
  });

  // Membership configuration
  const [membershipConfig, setMembershipConfig] = useState<MembershipConfig | null>(null);
  const [hasMembership, setHasMembership] = useState(false);
  const [isLoadingMembership, setIsLoadingMembership] = useState(true);
  const [membershipError, setMembershipError] = useState<string | null>(null);

  // Additional stats
  const [totalMembers, setTotalMembers] = useState(0);
  const [activeStores, setActiveStores] = useState(0);
  const [todayNewMembers, setTodayNewMembers] = useState(0);
  const [monthGrowth, setMonthGrowth] = useState(0);
  const [genderRatio, setGenderRatio] = useState<GenderRatio>({ male: 0, female: 0 });
  const [selectedPeriod, setSelectedPeriod] = useState('7days');
  const [dailyMembers, setDailyMembers] = useState<DailyMember[]>([]);
  const [topStores, setTopStores] = useState<TopStore[]>([]);
  const [benefitProducts, setBenefitProducts] = useState<BenefitProduct[]>([]);

  // Check if screen is mobile size
  MembershipFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  // 프로젝트 정보 로드
  useEffect(() => {
    const projectInfo = MembershipFormUtils.loadProjectDetail();
    if (projectInfo) {
      setProjectDetail(projectInfo);
    }
  }, []);

  // 멤버십 데이터 로드
  useEffect(() => {
    const loadMembershipData = async () => {
      if (!projectDetail) return;

      try {
        setIsLoadingMembership(true);
        setMembershipError(null);

        const projectCode = projectDetail.code || getProjectCode();
        if (!projectCode) {
          setMembershipError('프로젝트 코드가 없습니다.');
          setHasMembership(false);
          return;
        }

        const list = await getMembershipList(projectCode, 0, 1);
        if (list.totalCount === 0 || list.memberships.length === 0) {
          setHasMembership(false);
          return;
        }

        const membershipCode = list.memberships[0].code;
        const membership = await getMembershipDetail(projectCode, membershipCode);

        // 멤버십 상세 조회 응답값 콘솔 출력
        console.log('멤버십 상세 조회 응답:', membership);

        if (!membership) {
          setHasMembership(false);
        } else {
          // 멤버십이 존재하는 경우
          setHasMembership(true);
          
          const membershipConfigData: MembershipConfig = {
            rewardType: membership.rewardType || 'NONE',
            stampSetCount: membership.stampSetCount,
            voucherExchangeMethod: membership.voucherExchangeMethod,
            pointRatio: membership.pointRatio
          };
          
          // 디버깅: 설정된 membershipConfigData 확인
          console.log('멤버십 설정 데이터:', membershipConfigData);
          
          setMembershipConfig(membershipConfigData);

          // 통계 데이터 로드 (임시 데이터 - 실제로는 별도 API 호출)
          setStats({
            totalMembers: 1250,
            activeMembers: 980,
            newMembersThisMonth: 45,
            totalStamps: 5670,
            totalPoints: 125000,
            stampRedemptions: 89,
            pointRedemptions: 156
          });

          // 추가 통계 데이터
          setTotalMembers(1250);
          setActiveStores(12);
          setTodayNewMembers(8);
          setMonthGrowth(12.5);
          setGenderRatio({ male: 45, female: 55 });

          // 일별 회원 데이터
          setDailyMembers([
            { date: '01/15', count: 12 },
            { date: '01/16', count: 8 },
            { date: '01/17', count: 15 },
            { date: '01/18', count: 22 },
            { date: '01/19', count: 18 },
            { date: '01/20', count: 25 },
            { date: '01/21', count: 8 }
          ]);

          // 상위 매장 데이터
          setTopStores([
            { storeCode: 'ST001', storeName: '강남점', memberCount: 320, growthRate: 15.2 },
            { storeCode: 'ST002', storeName: '홍대점', memberCount: 280, growthRate: 8.7 },
            { storeCode: 'ST003', storeName: '신촌점', memberCount: 250, growthRate: -2.1 },
            { storeCode: 'ST004', storeName: '건대점', memberCount: 220, growthRate: 12.3 },
            { storeCode: 'ST005', storeName: '이대점', memberCount: 180, growthRate: 5.8 }
          ]);

          // 혜택 상품 데이터
          setBenefitProducts([
            { productCode: 'P001', productName: '아메리카노', benefitType: 'stamp', benefitValue: 1 },
            { productCode: 'P002', productName: '라떼', benefitType: 'stamp', benefitValue: 1 },
            { productCode: 'P003', productName: '카페모카', benefitType: 'point', benefitValue: 5 },
            { productCode: 'P004', productName: '프라푸치노', benefitType: 'point', benefitValue: 3 }
          ]);
        }
      } catch (error: any) {
        // 404 에러는 정상 처리되었으므로 로깅하지 않음
        const errorMessage = error?.message || '';
        const is404Error = errorMessage.includes('404') || errorMessage.includes('status: 404');
        
        if (!is404Error) {
          console.error('멤버십 데이터 로드 중 오류:', error);
          // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
          const displayMessage = CommonUtils.getNetworkErrorMessage(error);
          setMembershipError(displayMessage);
        }
        setHasMembership(false);
      } finally {
        setIsLoadingMembership(false);
      }
    };

    loadMembershipData();
  }, [projectDetail]);

  if (!projectDetail) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">프로젝트 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar 
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title="멤버십 대시보드"
          subtitle="멤버십 현황 및 통계를 확인하세요"
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          projectDetail={projectDetail}
        />

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {isLoadingMembership ? (
              /* 로딩 상태 */
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">멤버십 정보를 불러오는 중...</h3>
                <p className="text-gray-500">잠시만 기다려주세요.</p>
              </div>
            ) : membershipError ? (
              /* 에러 상태 */
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">오류가 발생했습니다</h3>
                <p className="text-gray-500 mb-6">{membershipError}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  다시 시도
                </button>
              </div>
            ) : !hasMembership ? (
              /* 멤버십 설정이 없는 경우 */
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <Settings className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">멤버십이 설정되지 않았습니다</h3>
                <p className="text-gray-500 mb-6">멤버십 혜택을 설정하여 고객에게 더 나은 서비스를 제공하세요.</p>
                <button
                  onClick={() => navigate('/memberships/create')}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  멤버십 설정하기
                </button>
              </div>
            ) : (
              <>
                {/* 멤버십 혜택 정보 */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">멤버십 혜택</h2>
                    <button 
                      onClick={() => navigate('/memberships')}
                      className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      수정
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {membershipConfig?.rewardType === 'STAMP' || membershipConfig?.rewardType === 'BOTH' ? (
                      (membershipConfig.stampSetCount || membershipConfig.voucherExchangeMethod) ? (
                        <div className="p-4 bg-blue-50 rounded-lg">
                          <div className="flex items-center mb-3">
                            <Stamp className="w-5 h-5 text-blue-600 mr-2" />
                            <h3 className="font-medium text-gray-900">스탬프 적립</h3>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">
                            {membershipConfig.stampSetCount || 0}개 적립 시
                          </p>
                          {membershipConfig.voucherExchangeMethod && (
                            <p className="text-sm font-medium text-blue-600">
                              {membershipConfig.voucherExchangeMethod}
                            </p>
                          )}
                        </div>
                      ) : null
                    ) : null}

                    {membershipConfig?.rewardType === 'POINT' || membershipConfig?.rewardType === 'BOTH' ? (
                      membershipConfig.pointRatio ? (
                        <div className="p-4 bg-green-50 rounded-lg">
                          <div className="flex items-center mb-3">
                            <Coins className="w-5 h-5 text-green-600 mr-2" />
                            <h3 className="font-medium text-gray-900">포인트 적립</h3>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">
                            결제 금액의 {membershipConfig.pointRatio || 0}%
                          </p>
                          <p className="text-sm text-gray-500">
                            최소 구매: 1,000원
                          </p>
                        </div>
                      ) : null
                    ) : null}
                  </div>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">총 회원수</span>
                      <Users className="w-4 h-4 text-gray-400" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{totalMembers.toLocaleString()}</p>
                    <div className="flex items-center mt-2 text-sm">
                      <ArrowUp className="w-3 h-3 text-green-500 mr-1" />
                      <span className="text-green-600">{monthGrowth}%</span>
                      <span className="text-gray-500 ml-1">지난달 대비</span>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">활성 매장</span>
                      <Store className="w-4 h-4 text-gray-400" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{activeStores}</p>
                    <p className="text-sm text-gray-500 mt-2">멤버십 사용 중</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">오늘 신규</span>
                      <UserPlus className="w-4 h-4 text-gray-400" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">+{todayNewMembers}</p>
                    <p className="text-sm text-gray-500 mt-2">신규 가입자</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">성비</span>
                      <Users className="w-4 h-4 text-gray-400" />
                    </div>
                    <div className="flex items-center mt-1">
                      <div className="flex-1">
                        <div className="flex justify-between text-xs mb-1">
                          <span>남성</span>
                          <span>{genderRatio.male}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${genderRatio.male}%` }} />
                        </div>
                      </div>
                      <div className="mx-2 text-gray-400">/</div>
                      <div className="flex-1">
                        <div className="flex justify-between text-xs mb-1">
                          <span>여성</span>
                          <span>{genderRatio.female}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-pink-600 h-2 rounded-full" style={{ width: `${genderRatio.female}%` }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Daily Members Chart */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-base font-semibold text-gray-900">일별 회원 가입</h3>
                      <select 
                        value={selectedPeriod}
                        onChange={(e) => setSelectedPeriod(e.target.value)}
                        className="text-sm border border-gray-300 rounded px-2 py-1"
                      >
                        <option value="7days">7일</option>
                        <option value="30days">30일</option>
                        <option value="90days">90일</option>
                      </select>
                    </div>
                    <div className="space-y-3">
                      {dailyMembers.map((day) => (
                        <div key={day.date} className="flex items-center">
                          <span className="text-xs text-gray-500 w-12">{day.date}</span>
                          <div className="flex-1 mx-3">
                            <div className="w-full bg-gray-200 rounded-full h-4">
                              <div 
                                className="bg-blue-600 h-4 rounded-full flex items-center justify-end px-2"
                                style={{ width: `${(day.count / 70) * 100}%` }}
                              >
                                <span className="text-xs text-white">{day.count}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Top Stores */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-base font-semibold text-gray-900 mb-4">매장별 회원수</h3>
                    <div className="space-y-3">
                      {topStores.map((store, index) => (
                        <div key={store.storeCode} className="flex items-center justify-between">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-500 w-6">{index + 1}</span>
                            <span className="text-sm text-gray-900 ml-2">{store.storeName}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-900 mr-2">
                              {store.memberCount.toLocaleString()}
                            </span>
                            <span className={`text-xs flex items-center ${
                              store.growthRate > 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {store.growthRate > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                              {Math.abs(store.growthRate)}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Benefit Products */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-base font-semibold text-gray-900 mb-4">혜택 적용 상품</h3>
                    <div className="space-y-3">
                      {benefitProducts.map((product) => (
                        <div key={product.productCode} className="flex items-center justify-between">
                          <span className="text-sm text-gray-900">{product.productName}</span>
                          <div className="flex items-center">
                            {product.benefitType === 'stamp' ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                <Stamp className="w-3 h-3 mr-1" />
                                스탬프 {product.benefitValue}개
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                <Coins className="w-3 h-3 mr-1" />
                                포인트 {product.benefitValue}%
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                      <button className="w-full text-sm text-blue-600 hover:text-blue-700 text-center pt-2 border-t">
                        전체 상품 보기
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default MembershipDashboard;